import { useCallback, useContext } from 'react';

import { AutoOrderActionContext } from '../../components/AutoOrderActionProvider';

import usePauseAutoOrder from './usePauseAutoOrder';
import useResumeAutoOrder from './useResumeAutoOrder';
import useAutoOrder from '../useAutoOrder';

const OPEN_AMEND = 'AMEND';
const OPEN_WITHDRAW = 'WITHDRAW';
const OPEN_REACTIVATE_EXPIRED = 'REACTIVATE_EXPIRED';

const ORDER_AUTO = 'AUTO';

/**
 * @param {import('@services/api/stock/order/auto/order').AutoOrder} autoOrder
 */
function useAutoOrderAction(autoOrder) {
  const { setPopup } = useContext(AutoOrderActionContext);
  const pauseAutoOrder = usePauseAutoOrder(autoOrder.id);
  const resumeAutoOrder = useResumeAutoOrder(autoOrder.id);
  const { refetch } = useAutoOrder({ accountType: autoOrder.account_type });

  const accountType = autoOrder.account_type === 'MARGIN' ? 'MARGIN_TRADING' : 'REGULAR';

  const pause = useCallback(async () => {
    try {
      await pauseAutoOrder();
    } catch (err) {}

    return refetch();
  }, [pauseAutoOrder, refetch]);

  const resume = useCallback(async () => {
    if (autoOrder.order_status === 'EXPIRED') {
      setPopup({
        open: OPEN_REACTIVATE_EXPIRED,
        data: { accountType, orderType: ORDER_AUTO, orderDetail: autoOrder },
      });
    }

    if (autoOrder.order_status === 'PAUSED') {
      try {
        await resumeAutoOrder();
      } catch (err) {}

      return refetch();
    }
  }, [autoOrder, setPopup, accountType, refetch]);

  const amend = useCallback(() => {
    setPopup({
      open: OPEN_AMEND,
      data: { accountType, orderType: ORDER_AUTO, orderDetail: autoOrder },
    });
  }, [autoOrder, accountType, setPopup]);

  const withdraw = useCallback(() => {
    setPopup({
      open: OPEN_WITHDRAW,
      data: { accountType, orderType: ORDER_AUTO, orderDetail: autoOrder },
    });
  }, [autoOrder, accountType, setPopup]);

  const toggle = useCallback(() => {
    if (autoOrder.order_status === 'OPEN') {
      return pause();
    }
    return resume();
  }, [autoOrder, resume, pause]);

  return { pause, resume, toggle, amend, withdraw };
}

export default useAutoOrderAction;
